import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to OCYM Blood Donors Network ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our blood donation platform.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">2.1 Personal Information</h3>
                <p className="text-gray-700 mb-2">We collect the following personal information when you register as a donor:</p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Blood group</li>
                  <li>Age and weight</li>
                  <li>Location and district</li>
                  <li>Parish affiliation (optional)</li>
                  <li>Last donation date (if applicable)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">2.2 Health Information</h3>
                <p className="text-gray-700">
                  We collect limited health-related information necessary for blood donation coordination, including your blood group, donation history, and eligibility status.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">2.3 Usage Data</h3>
                <p className="text-gray-700">
                  We automatically collect certain information when you use our platform, including your IP address, browser type, device information, and usage patterns.
                </p>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-3">We use your information for the following purposes:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>To facilitate blood donation requests and connect donors with recipients</li>
              <li>To verify donor eligibility and maintain donation records</li>
              <li>To send notifications about urgent blood requests in your area</li>
              <li>To communicate with you about your account and our services</li>
              <li>To improve our platform and user experience</li>
              <li>To comply with legal obligations and prevent fraud</li>
              <li>To generate anonymized statistics about blood donation activities</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">4.1 With Blood Recipients</h3>
                <p className="text-gray-700">
                  Based on your privacy settings, we may share your contact information with individuals requesting blood donations. You control what information is visible through your privacy preferences.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">4.2 With Healthcare Facilities</h3>
                <p className="text-gray-700">
                  We may share relevant information with hospitals and blood banks to facilitate the donation process.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">4.3 Legal Requirements</h3>
                <p className="text-gray-700">
                  We may disclose your information if required by law, court order, or governmental authority.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">4.4 We Do Not Sell Your Data</h3>
                <p className="text-gray-700 font-semibold">
                  We never sell, rent, or trade your personal information to third parties for marketing purposes.
                </p>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-700 mb-3">
              We implement appropriate technical and organizational measures to protect your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication using Firebase Authentication</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and user permissions</li>
              <li>Secure cloud infrastructure (Firebase/Google Cloud)</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights and Choices</h2>
            <p className="text-gray-700 mb-3">You have the following rights regarding your personal information:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update or correct your information through your profile</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data</li>
              <li><strong>Privacy Settings:</strong> Control what information is visible to others</li>
              <li><strong>Notification Preferences:</strong> Manage how and when you receive notifications</li>
              <li><strong>Opt-out:</strong> Unsubscribe from non-essential communications</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Data Retention</h2>
            <p className="text-gray-700">
              We retain your personal information for as long as your account is active or as needed to provide services. If you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal purposes.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-700">
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          {/* International Users */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. International Data Transfers</h2>
            <p className="text-gray-700">
              Your information may be transferred to and processed in countries other than India, including the United States where our cloud service providers are located. We ensure appropriate safeguards are in place for such transfers.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Privacy Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
              <div className="space-y-2">
                <p className="text-gray-800">
                  <strong>OCYM Blood Donors Network</strong>
                </p>
                <p className="text-gray-700">
                  <strong>Email:</strong> <a href="mailto:support@ocymblood.org" className="text-blue-600 hover:text-blue-700">support@ocymblood.org</a>
                </p>
                <p className="text-gray-700">
                  <strong>Phone:</strong> <a href="tel:9809034879" className="text-blue-600 hover:text-blue-700">+91 9809034879</a>
                </p>
                <p className="text-gray-700">
                  <strong>Website:</strong> <a href="https://ocymonline.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">ocymonline.org</a>
                </p>
              </div>
            </div>
          </section>

          {/* GDPR Compliance */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. GDPR Compliance (For EU Users)</h2>
            <p className="text-gray-700 mb-3">
              If you are located in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR):
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Right to be informed about data processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to restrict processing</li>
              <li>Right to lodge a complaint with a supervisory authority</li>
            </ul>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium shadow-lg"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
